/**
 * IconButton — circular or squared icon-only button.
 *
 * Ported 1:1 from `tw-components.jsx:94`. Deviations are mechanical only: `cx` and
 * `Icon` are imported rather than file-scope, and there's no `import React` (this
 * app uses the automatic JSX runtime and the component has no hooks).
 *
 * **Its base class list contains `relative`.** Anything positioning an IconButton
 * from the outside must use `!absolute` — Tailwind emits position utilities in the
 * order `static, fixed, absolute, relative, sticky`, so at equal specificity
 * `.relative` wins and the button silently falls back into normal flow. `ProductCard`
 * does exactly this for its wishlist heart; see `COMPONENTS.md:31`.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

const IB_SIZE = {
  sm: "w-[36px] h-[36px]",
  md: "w-[44px] h-[44px]",
  lg: "w-[52px] h-[52px]",
};

const IB_VAR = {
  ghost:
    "text-ink-2 bg-transparent enabled:hover:bg-[var(--action-neutral-hover)] enabled:hover:text-ink",
  solid:
    "bg-[var(--action-primary)] text-white enabled:hover:bg-[var(--action-primary-hover)] enabled:hover:text-white enabled:hover:shadow-brand",
  soft: "bg-orange-50 text-orange-700 enabled:hover:bg-orange-100 enabled:hover:text-orange-700",
  outline:
    "text-ink-2 bg-transparent border-line enabled:hover:border-blue-500 enabled:hover:text-blue-700 enabled:hover:bg-blue-50",
};

const IB_ICON_SIZE = { sm: 20, md: 22, lg: 24 };

export function IconButton({
  icon,
  variant = "ghost",
  size = "md",
  shape = "round",
  disabled = false,
  "aria-label": ariaLabel,
  onClick,
  style = {},
  className = "",
  ...rest
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={style}
      className={cx(
        "dnd-iconbtn group/ib relative inline-flex items-center justify-center border-[1.5px] border-transparent cursor-pointer overflow-hidden",
        "[transition:transform_var(--duration-base)_var(--ease-spring),background_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),border-color_var(--duration-fast)_var(--ease-standard)]",
        "enabled:active:scale-[0.9] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] disabled:opacity-45 disabled:cursor-not-allowed",
        IB_SIZE[size] || IB_SIZE.md,
        shape === "square"
          ? "rounded-[var(--radius-control)]"
          : "rounded-[var(--radius-pill)]",
        IB_VAR[variant] || IB_VAR.ghost,
        className
      )}
      {...rest}
    >
      {typeof icon === "string" ? (
        <Icon
          name={icon}
          size={IB_ICON_SIZE[size]}
          weight={400}
          className="[transition:transform_var(--duration-base)_var(--ease-spring)] group-hover/ib:rotate-[-8deg] group-hover/ib:scale-[1.08]"
        />
      ) : (
        icon
      )}
    </button>
  );
}
